import { BaseModuleSettings } from '@mbrtargeting/metatag-config-types';
import { generateUUID } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { logger } from '../../../loader/essentials/logger.js';
import { AdslotRegistry } from '../../../loader/modules/adslot-registry.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { toAdSizeString } from '../../../utils/adsize-helper.js';
import { markSdg } from '../../../utils/performance.js';
import { CorsRequestOptions, sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { Adslot, isAdslotInstance } from '../adslot-controller/adslot.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';

const log = logger.logGroup({ prefix: 'SAM' });

declare const window: Window & speedcurve.Window;

interface SamTargetingRequest {
    /** id needs to be unique id for each request */
    id: string;
    /** adunits */
    adUnits: string[];
    slots: Array<{
        /** the slot's position (name) */
        position: string
        /** available sizes on the slot */
        dfpsizes: string[];
    }>;
}

interface SamTargetingResponse {
    stroeerCoreContext?: {
        [name: string]: string[] | string | null
    }
}

function recordMetrics(sentTime: DOMHighResTimeStamp, errored: boolean) {
    // https://support.speedcurve.com/docs/rum-js-api
    if (window.LUX?.addData) {
        window.LUX.addData('sam-request-error', errored);
    } else if (errored) {
        // we don't record the timing in this case
        log.warn('LUX API is not available for segmenting');
        return;
    }

    markSdg('sent-request-to-sam_mt', {
        startTime: sentTime
    });
    markSdg('received-answer-from-sam_mt');
}

function createRequestBody(adslots: Array<Adslot>): string {
    const anyAdslot = adslots[0] as Adslot;

    const samRequest: SamTargetingRequest = {
        id: generateUUID(),
        adUnits: anyAdslot.getAdUnits(),
        slots: adslots.map(adslot => ({
            position: adslot.slotName,
            dfpsizes: adslot.getSizes().map(toAdSizeString),
        })
        )
    };

    return JSON.stringify(samRequest);
}

/**
 * "Sponsorship Availability Manager" Targeting
 *
 * This module supports Sponsorship Campaigns (high-priority, exclusive campaigns).
 * Guaranteeing the delivery of the campaign's ads on all the placements is a key characteristic (often called
 * homepage takeover or roadblock). To achieve this, we need to detect whether a sponsorship campaign is available
 * before starting the prebid auction.
 *
 * The detection is determined by the 'sam' endpoint. This module makes a request to this endpoint and populates the
 * global targeting with the key-values provided in its response. When the key-values indicate a sponsorship campaign,
 * Yieldlove prebid core and Stroeer prebid bidder will take the necessary actions (e.g. exclude competitors, Stroeer
 * always win regardless of the CPM, bypass GAM & directly render, etc).
 *
 * This is currently an EXPERIMENTAL and is disabled by default. We primarily want to test to see if this approach will
 * be fast and reliable enough, so we record metrics with SpeedCurve here (LUX and the Performance API).
 */
@injectionTarget()
export class SamTargeting {

    @inject() private configResolver!: ConfigResolver;
    @inject() private globalTargeting!: GlobalTargeting;
    @inject() private adslotRegistry!: AdslotRegistry;
    @inject() private consentCache!: ConsentCache;

    private readonly moduleSettings?: BaseModuleSettings;

    constructor() {
        this.moduleSettings = this.configResolver.getModuleSettings('SAM');
    }

    @onPhase({ phase: Phase.TARGETING })
    async requestSponsorshipAvailability() {
        const { active = false, iabVendorId = 0 } = this.moduleSettings ?? {};

        if (!active) return;

        const allowed = await this.consentCache.whenConsentConditions([
            { gdprApplies: false },
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
        ]);

        if (!allowed) {
            log.info('no consent');
            return;
        }

        const adslots = this.adslotRegistry.getAdslots().filter(isAdslotInstance);

        if (!adslots.length) {
            return;
        }

        const requestOptions: CorsRequestOptions = {
            src: 'https://sam.adscale.de/scc',
            method: 'POST',
            withCredentials: true,
            body: createRequestBody(adslots),
            // TODO: change to Simple CORS when sam endpoint supports it.
            // For now, we want Preflight CORS without the 'X-Requested-With' header.
            contentType: 'application/json'
        };

        const sentTime = performance.now();

        let samResponse: SamTargetingResponse;
        try {
            samResponse = await sendCorsRequestPromise<SamTargetingResponse>(requestOptions);
        } catch (ignore) {
            recordMetrics(sentTime, true);
            log.warn('request failed');
            return;
        }

        recordMetrics(sentTime, false);

        Object.entries(samResponse.stroeerCoreContext || {}).forEach(([key, value]) => {
            if (value != null) {
                this.globalTargeting.addValues(key, value instanceof Array ? value : [value]);
            }
        });

        log.debug('response %o', [samResponse]);
    }
}
